import PropTypes from 'prop-types';
import { Activity, useEffect, useRef, useState } from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// project imports
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import useConfig from 'hooks/useConfig';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

export default function NavItem({ item, level, isParents = false, setSelectedID }) {
  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));
  const ref = useRef(null);

  const { pathname } = useLocation();
  const {
    state: { borderRadius }
  } = useConfig();

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const isSelected = !!matchPath({ path: item?.link ? item.link : item.url, end: false }, pathname);

  const [hoverStatus, setHover] = useState(false);

  const compareSize = () => {
    const compare = ref.current && ref.current.scrollWidth > ref.current.clientWidth;
    setHover(compare);
  };

  useEffect(() => {
    compareSize();
    window.addEventListener('resize', compareSize);
    window.removeEventListener('resize', compareSize);
  }, []);

  const Icon = item?.icon;
  const itemIcon = item?.icon ? (
    <Icon stroke={1.5} size={drawerOpen ? '17px' : '20px'} style={{ ...(isParents && { fontSize: 17, stroke: '1.5' }) }} />
  ) : (
    <span style={{
      width: isSelected ? 5 : 3,
      height: isSelected ? 5 : 3,
      borderRadius: '50%',
      backgroundColor: isSelected ? '#FF8000' : 'currentColor',
      display: 'inline-block',
      flexShrink: 0,
      opacity: isSelected ? 1 : 0.4
    }} />
  );

  let itemTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  const itemHandler = () => {
    if (downMD) handlerDrawerOpen(false);

    if (isParents && setSelectedID) {
      setSelectedID();
    }
  };

  return (
    <>
      <ListItemButton
        component={Link}
        to={item.url}
        target={itemTarget}
        disabled={item.disabled}
        disableRipple={!drawerOpen}
        sx={{
          zIndex: 1201,
          borderRadius: '7px',
          mb: 0.2,
          px: 1,
          py: 0.5,
          minHeight: 32,
          mr: '2px',
          ml: drawerOpen && level !== 1 ? `${level * 8 + 2}px` : '2px',
          ...(!drawerOpen && { pl: 1 }),
          position: 'relative',
          transition: 'all 0.15s ease-in-out',
          color: isSelected 
            ? (theme.palette.mode === 'dark' ? '#2DD4BF' : '#0D9488') 
            : (theme.palette.mode === 'dark' ? '#94A3B8' : '#475569'),
          background: isSelected 
            ? (theme.palette.mode === 'dark' ? 'rgba(20, 184, 166, 0.15)' : '#F0FDFA') 
            : 'transparent',
          borderLeft: isSelected 
            ? (theme.palette.mode === 'dark' ? '3px solid #2DD4BF' : '3px solid #14B8A6') 
            : '3px solid transparent',
          boxShadow: isSelected ? '0 1px 2px rgba(20, 184, 166, 0.08)' : 'none',
          '&:hover': {
            background: isSelected 
              ? (theme.palette.mode === 'dark' ? 'rgba(20, 184, 166, 0.22)' : '#F0FDFA') 
              : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0,0,0,0.03)'),
            color: isSelected 
              ? (theme.palette.mode === 'dark' ? '#2DD4BF' : '#0D9488') 
              : (theme.palette.mode === 'dark' ? '#F8FAFC' : '#1E293B')
          },
          '&.Mui-selected': {
            background: theme.palette.mode === 'dark' ? 'rgba(20, 184, 166, 0.15)' : '#F0FDFA',
            color: theme.palette.mode === 'dark' ? '#2DD4BF' : '#0D9488',
            borderLeft: theme.palette.mode === 'dark' ? '3px solid #2DD4BF' : '3px solid #14B8A6',
            '&:hover': {
              background: theme.palette.mode === 'dark' ? 'rgba(20, 184, 166, 0.22)' : '#CCFBF1'
            }
          },
          ...(!drawerOpen && level !== 1 && {
            py: level === 1 ? 0 : 0.5,
          })
        }}
        selected={isSelected}
        onClick={() => itemHandler()}
      >
        <ButtonBase aria-label="theme-icon" sx={{ borderRadius: `${borderRadius}px` }} disableRipple={drawerOpen}>
          <ListItemIcon
            sx={{
              minWidth: level === 1 ? 26 : 14,
              color: isSelected 
                ? (theme.palette.mode === 'dark' ? '#2DD4BF' : '#0D9488') 
                : (theme.palette.mode === 'dark' ? '#94A3B8' : '#64748B'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'color 0.15s ease-in-out',
              '& svg': {
                strokeWidth: 1.75
              }
            }}
          >
            {itemIcon}
          </ListItemIcon>
        </ButtonBase>

        {(drawerOpen || (!drawerOpen && level !== 1)) && (
          <Tooltip title={item.title} disableHoverListener={!hoverStatus}>
            <ListItemText
              primary={
                <Typography
                  ref={ref}
                  noWrap
                  variant={isSelected ? 'h5' : 'body1'}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '100%',
                    fontSize: '0.8125rem',
                    fontWeight: isSelected ? 600 : 450,
                    color: 'inherit'
                  }}
                >
                  {item.title}
                </Typography>
              }
              secondary={
                item.caption && (
                  <Typography
                    variant="caption"
                    gutterBottom
                    sx={{
                      display: 'block',
                      fontSize: '0.6875rem',
                      fontWeight: 500,
                      color: 'text.secondary',
                      textTransform: 'capitalize',
                      lineHeight: 1.66
                    }}
                  >
                    {item.caption}
                  </Typography>
                )
              }
            />
          </Tooltip>
        )}

        <Activity mode={drawerOpen && item.chip ? 'visible' : 'hidden'}>
          <Chip
            color={item.chip?.color}
            variant={item.chip?.variant}
            size={item.chip?.size}
            label={item.chip?.label}
            avatar={
              <Activity mode={item.chip?.avatar ? 'visible' : 'hidden'}>
                <Avatar>{item.chip?.avatar}</Avatar>
              </Activity>
            }
          />
        </Activity>
      </ListItemButton>
    </>
  );
}

NavItem.propTypes = { item: PropTypes.any, level: PropTypes.number, isParents: PropTypes.bool, setSelectedID: PropTypes.func };
